import React, { useState, useEffect } from "react";
import { Menu, X, ChevronDown, ChevronRight, User } from "lucide-react";
import menuData from "./menu.json";
import { Service, MenuData } from "./types";
import { authService } from "../../../services/auth.service";

const iconMap: { [key: string]: string } = {
  "Pelayanan Rawat Jalan": "🏥",
  "Griya Jamu": "🌿",
  "Laboratorium Klinik": "🔬",
  "Akupuntur & Akupresure": "💆",
  "Konsultasi Gizi": "🥗",
  "Wisata Edukasi Tanaman Obat": "🌺",
  "Pelatihan & Workshop Pemanfaatan Tanaman Obat": "👨‍🏫",
  "Produk & Layanan Bahan Baku Tanaman Obat": "🌾",
};

const typedMenuData = menuData as MenuData;

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [servicesMenuOpen, setServicesMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [activeMobileSubmenu, setActiveMobileSubmenu] = useState<string | null>(
    null
  );
  const [username, setUsername] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [dynamicServices, setDynamicServices] = useState<Service[]>(typedMenuData.services);

  const isAuthenticated = authService.isAuthenticated();
  const { role } = authService.getAuthState();
  const isAdmin = role === "admin";

  // Throttle scroll event
  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle click outside for dropdowns with improved performance
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (profileMenuOpen && !target.closest(".profile-menu-container")) {
        setProfileMenuOpen(false);
      }
      if (servicesMenuOpen && !target.closest(".services-menu-container")) {
        setServicesMenuOpen(false);
      }
    };

    if (profileMenuOpen || servicesMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [profileMenuOpen, servicesMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const storageType = localStorage.getItem("storageType");
    const storage = storageType === "local" ? localStorage : sessionStorage;
    setUsername(storage.getItem("username"));
    setEmail(storage.getItem("email"));
    setLoading(false);

    // Fetch dynamic services
    const fetchServices = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/service`);
        const result = await response.json();
        if (result.data && Array.isArray(result.data)) {
          const fetchedServices: Service[] = result.data.map((s: any) => ({
            id: s.id + 1000, // offset to avoid duplicate keys with hardcoded ones
            title: s.name,
            description: s.description ? s.description.replace(/<[^>]*>?/gm, '').substring(0, 50) + "..." : "Layanan",
            icon: "📋",
            href: s.name,
          }));
          
          // You can choose to replace or combine. We will replace the hardcoded ones except perhaps some static ones if needed, 
          // or just replace entirely to reflect DB. Let's just use the DB ones if they exist.
          if (fetchedServices.length > 0) {
            setDynamicServices(fetchedServices);
          }
        }
      } catch (err) {
        console.error("Failed to fetch services for navbar:", err);
      }
    };
    fetchServices();
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/login";
  };

  const getIconForService = (serviceName: string): string => {
    const matchingIcon = Object.entries(iconMap).find(([key]) =>
      serviceName.toLowerCase().includes(key.toLowerCase())
    );
    return matchingIcon ? matchingIcon[1] : "📋";
  };

  const createSlug = (name: string): string =>
    encodeURIComponent(
      name
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^\w\-&]+/g, "")
        .replace(/\-\-+/g, "-")
        .replace(/^-+/, "")
        .replace(/-+$/, "")
    );

  const renderServiceItem = (service: Service) => (
    <a
      key={service.id}
      href={`/layanan/${createSlug(service.href)}`}
      className="p-4 transition-all duration-300 bg-white rounded-lg md:p-6 hover:bg-gray-50 group"
    >
      <div className="flex items-start gap-3 md:gap-4">
        <span className="text-3xl md:text-4xl">
          {getIconForService(service.title)}
        </span>
        <div className="flex-1 min-w-0">
          <h4 className="mb-1 text-base font-semibold text-gray-900 truncate md:text-lg group-hover:text-blue-600 md:mb-2">
            {service.title}
          </h4>
          <p className="text-sm text-gray-600 line-clamp-2">
            {service.description}
          </p>
        </div>
      </div>
    </a>
  );

  const renderMobileServiceItem = (service: Service) => (
    <a
      key={service.id}
      href={`/layanan/${createSlug(service.href)}`}
      className="flex items-start gap-3 p-3 transition-all duration-300 border-b border-gray-100 hover:bg-gray-50"
    >
      <span className="flex-shrink-0 text-2xl sm:text-3xl">
        {getIconForService(service.title)}
      </span>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-semibold text-gray-900 truncate sm:text-base">
          {service.title}
        </h4>
        <p className="text-xs text-gray-600 sm:text-sm line-clamp-2">
          {service.description}
        </p>
      </div>
    </a>
  );

  const renderDesktopMenuItem = (item: string) => {
    if (item === "Layanan") {
      return (
        <div className="services-menu-container">
          <button
            className="relative flex items-center gap-1 text-sm font-medium text-gray-700 group"
            onMouseEnter={() => setServicesMenuOpen(true)}
            onMouseLeave={() => setServicesMenuOpen(false)}
          >
            {item}
            <ChevronDown
              className={`w-4 h-4 transition-transform duration-300 ${
                servicesMenuOpen ? "rotate-180" : ""
              }`}
            />
            <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-blue-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
          </button>

          <div
            className={`absolute left-1/2 -translate-x-1/2 mt-4 w-[90vw] max-w-4xl bg-white rounded-xl shadow-xl border border-gray-100 transition-all duration-300 ${
              servicesMenuOpen
                ? "opacity-100 translate-y-0 visible"
                : "opacity-0 -translate-y-2 invisible"
            }`}
            onMouseEnter={() => setServicesMenuOpen(true)}
            onMouseLeave={() => setServicesMenuOpen(false)}
          >
            <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2 md:gap-4 md:p-6">
              {dynamicServices.map(renderServiceItem)}
            </div>
          </div>
        </div>
      );
    }

    return (
      <a
        href={
          item === "Beranda"
            ? "/"
            : `/${item.toLowerCase().replace(/\s+/g, "-")}`
        }
        className="relative text-sm font-medium text-gray-700 group"
      >
        {item}
        <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-blue-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
      </a>
    );
  };

  const AuthButtons = () => {
    if (loading) return null;

    if (isAuthenticated) {
      return (
        <div className="relative profile-menu-container">
          <button
            className="flex items-center gap-2 px-2 py-2 text-gray-700 transition-colors duration-300 rounded-lg hover:text-blue-600 sm:px-3 hover:bg-gray-50"
            onClick={() => setProfileMenuOpen(!profileMenuOpen)}
          >
            <User className="w-5 h-5" />
            <span className="hidden text-sm font-medium sm:block">
              {username}
            </span>
            <ChevronDown
              className={`w-4 h-4 transition-transform duration-300 ${
                profileMenuOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          <div
            className={`absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 transition-all duration-300 ${
              profileMenuOpen
                ? "opacity-100 translate-y-0 visible"
                : "opacity-0 -translate-y-2 invisible"
            }`}
          >
            <div className="p-4 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-900 truncate">
                {username}
              </p>
              <p className="mt-1 text-xs text-gray-500 truncate">{email}</p>
            </div>
            <div className="py-2">
              {isAdmin && (
                <a
                  href="/dashboard"
                  className="flex items-center px-4 py-2 text-sm text-gray-700 transition-colors duration-200 hover:bg-gray-50 hover:text-blue-600"
                >
                  Dashboard
                </a>
              )}
              <a
                href="/profile"
                className="flex items-center px-4 py-2 text-sm text-gray-700 transition-colors duration-200 hover:bg-gray-50 hover:text-blue-600"
              >
                Profil Saya
              </a>
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-4 py-2 text-sm text-red-600 transition-colors duration-200 hover:bg-red-50"
              >
                Keluar
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2 sm:gap-3">
      </div>
    );
  };

  const renderMobileMenu = () => (
    <div
      className={`fixed inset-0 bg-gray-800/50 backdrop-blur-sm z-50 transition-all duration-300 ${
        mobileMenuOpen ? "opacity-100 visible" : "opacity-0 invisible"
      }`}
      onClick={() => setMobileMenuOpen(false)}
    >
      <div
        className={`fixed top-0 right-0 w-full max-w-sm h-full bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${
          mobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <img
            src="/assets/logo.webp"
            alt="Logo"
              className="w-12 h-12 sm:h-12 sm:w-12 rounded-xl"
          />
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 text-gray-500 transition-colors duration-200 rounded-lg hover:text-gray-700 hover:bg-gray-100"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="overflow-y-auto h-[calc(100%-4rem)] pb-20">
          <div className="p-4">
            {typedMenuData.menuItems.map((item) => (
              <div key={item} className="mb-2">
                {item === "Layanan" ? (
                  <div>
                    <button
                      onClick={() =>
                        setActiveMobileSubmenu(
                          activeMobileSubmenu === "services" ? null : "services"
                        )
                      }
                      className="flex items-center justify-between w-full p-3 text-left text-gray-700 transition-colors duration-200 rounded-lg hover:bg-gray-50"
                    >
                      <span className="text-sm font-medium sm:text-base">
                        Layanan
                      </span>
                      <ChevronDown
                        className={`w-5 h-5 transition-transform duration-300 ${
                          activeMobileSubmenu === "services" ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    <div
                      className={`mt-2 space-y-1 transition-all duration-300 ${
                        activeMobileSubmenu === "services" ? "block" : "hidden"
                      }`}
                    >
                      {dynamicServices.map(renderMobileServiceItem)}
                    </div>
                  </div>
                ) : (
                  <a
                    href={
                      item === "Beranda"
                        ? "/"
                        : `/${item.toLowerCase().replace(" ", "-")}`
                    }
                    className="block p-3 text-sm font-medium text-gray-700 transition-colors duration-200 rounded-lg sm:text-base hover:bg-gray-50"
                  >
                    {item}
                  </a>
                )}
              </div>
            ))}
          </div>

          <div className="p-4 border-t border-gray-100">
            {isAuthenticated ? (
              <div className="space-y-3">
                {isAdmin && (
                  <a
                    href="/dashboard"
                    className="block w-full p-3 text-sm text-center text-blue-600 transition-colors duration-200 rounded-lg hover:bg-blue-50"
                  >
                    Dashboard
                  </a>
                )}
                <a
                  href="/profile"
                  className="block w-full p-3 text-sm text-center text-blue-600 transition-colors duration-200 rounded-lg hover:bg-blue-50"
                >
                  Profil Saya
                </a>
                <button
                  onClick={handleLogout}
                  className="block w-full p-3 text-sm text-center text-red-600 transition-colors duration-200 rounded-lg hover:bg-red-50"
                >
                  Keluar
                </button>
              </div>
            ) : (
              <div className="space-y-3">
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
          isScrolled
            ? "bg-white shadow-md py-9"
            : "bg-white/80 backdrop-blur-sm py-3 sm:py-4"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-8 flex items-center justify-between">
          
          <a href="/" className="flex-shrink-0">
            <img
              src="/assets/logo.webp"
              className="w-12 h-12 sm:h-12 sm:w-12 rounded-xl"
              alt="Logo"
            />
          </a>

          <div className="items-center justify-center flex-1 hidden gap-6 ml-12 lg:flex xl:gap-8">
            {typedMenuData.menuItems.map((item) => (
              <div key={item} className="relative">
                {renderDesktopMenuItem(item)}
              </div>
            ))}
          </div>

          <div className="hidden lg:block">
            <AuthButtons />
          </div>

          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 text-gray-500 transition-colors duration-200 rounded-lg lg:hidden hover:text-gray-700 hover:bg-gray-100"
            aria-label="Menu"
          >
            <Menu className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>
      </nav>

      {renderMobileMenu()}
    </>
  );
}
